import React, { useState } from 'react';
import { DollarSign, Coffee, Heart, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Card, CardContent } from './ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

const TIP_PACKAGES = [
  { id: 'small', amount: 1, label: 'Coffee', emoji: '☕', icon: Coffee, color: 'from-yellow-500 to-orange-500' },
  { id: 'medium', amount: 5, label: 'Lunch', emoji: '🍔', icon: DollarSign, color: 'from-green-500 to-emerald-500' },
  { id: 'large', amount: 10, label: 'Support', emoji: '❤️', icon: Heart, color: 'from-pink-500 to-rose-500' },
  { id: 'huge', amount: 25, label: 'Mega Support', emoji: '⭐', icon: Star, color: 'from-purple-500 to-indigo-500' }
];

const TipButton = ({ creatorId, creatorHandle, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [processing, setProcessing] = useState(false);

  const handleTip = async (packageId) => {
    try {
      setProcessing(true);
      
      // Get origin URL from browser
      const originUrl = window.location.origin;
      
      // Create tip session
      const response = await axios.post(`${API}/tip/create-session`, {
        package_id: packageId,
        creator_id: creatorId,
        origin_url: originUrl
      });
      
      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
      
    } catch (error) {
      console.error('Error creating tip session:', error);
      toast.error('Failed to initiate tip. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        className={`bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-bold ${className}`}
      >
        <DollarSign className="w-4 h-4 mr-2" />
        Tip Creator
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-yellow-500" />
              Support {creatorHandle || 'this creator'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Show your appreciation with a tip! 100% goes to the creator.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3 mt-4">
            {TIP_PACKAGES.map((pkg) => {
              const Icon = pkg.icon;
              return (
                <Card
                  key={pkg.id}
                  className="bg-gray-800 border-gray-700 hover:border-orange-500 transition-all cursor-pointer group"
                  onClick={() => !processing && handleTip(pkg.id)}
                >
                  <CardContent className="p-4 text-center">
                    <div className={`w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-r ${pkg.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-2xl font-bold mb-1">${pkg.amount}</div>
                    <div className="text-sm text-gray-400">{pkg.label}</div>
                    <div className="text-xl mt-2">{pkg.emoji}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p className="text-sm text-blue-400 flex items-center gap-2">
              <Heart className="w-4 h-4" />
              Your tip helps creators continue making amazing content!
            </p>
          </div>

          {processing && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
                <p className="text-white">Redirecting to payment...</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TipButton;
